/**
 * useMenuData
 * ───────────
 * Shared hook for the three /menu templates (compact, photo-grid,
 * accordion). Fetches categories + items, exposes a time-slot switcher
 * (All / Breakfast / Lunch / Dinner), and returns items already filtered
 * by the active slot.
 *
 * The time slot defaults to whatever the customer's clock says, but every
 * template surfaces a chip row so a morning user can preview the dinner
 * menu. Items with no `time_slots` array (legacy data) are visible under
 * every slot — never hide content silently.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

export const TIME_SLOTS = [
  { value: "all",       label: "All menus" },
  { value: "breakfast", label: "Breakfast", from: 0,  to: 11 },
  { value: "lunch",     label: "Lunch",     from: 11, to: 16 },
  { value: "dinner",    label: "Dinner",    from: 16, to: 24 },
];

export function currentSlot(now = new Date()) {
  const h = now.getHours();
  for (const s of TIME_SLOTS) {
    if (s.value === "all") continue;
    if (h >= s.from && h < s.to) return s.value;
  }
  return "all";
}

export function passesSlot(item, slot) {
  if (slot === "all") return true;
  const slots = Array.isArray(item?.time_slots) ? item.time_slots : [];
  if (slots.length === 0) return true;          // legacy → always visible
  if (slots.includes("all-day")) return true;   // all-day items show everywhere
  return slots.includes(slot);
}

export function useMenuData() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slot, setSlot] = useState(() => currentSlot());

  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([
          api.get("/categories"),
          api.get("/menu", { params: { available_only: true } }),
        ]);
        setCats(c.data || []);
        setItems(m.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visibleCats = useMemo(() => {
    const passing = items.filter((it) => passesSlot(it, slot));
    const catIds = new Set(passing.map((it) => it.category_id));
    return (cats || []).filter((c) => catIds.has(c.id));
  }, [cats, items, slot]);

  const itemsByCat = useMemo(() => {
    const out = {};
    items.filter((it) => passesSlot(it, slot)).forEach((it) => {
      (out[it.category_id] ||= []).push(it);
    });
    Object.values(out).forEach((arr) =>
      arr.sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || (a.name || "").localeCompare(b.name)),
    );
    return out;
  }, [items, slot]);

  return { cats: visibleCats, allCats: cats, itemsByCat, loading, slot, setSlot };
}
